import { c as _c } from "react/compiler-runtime";
/* eslint-disable import/order */
import '../style/global.css'; // This import MUST be on top
import { captureException } from '@sentry/nextjs';
import App, { AppContext, AppInitialProps } from 'next/app';
import Head from 'next/head';
import { didomiScriptElements } from '@sbt-web/didomi';
import { ClientHintsProvider, useViewport, ViewportProvider } from '@sbt-web/hooks';
import { createOptimizelyInstance, OptimizelySubitoProvider } from '@sbt-web/houston-wrapper';
import { HTTPStatusCode } from '@sbt-web/network/types';
import { isBrowser } from '@sbt-web/utils';
import { generateMetadata } from '@sbt-web/adv';
import ErrorPage from '@/client/components/Scaffolding/Errors';
import FourTen from '@/client/components/Scaffolding/Errors/410';
import Layout from '@/client/components/Scaffolding/Layout';
import { WebVitalsLab } from '@/client/components/WebVitalsLab';
import { UserProvider } from '@/client/contexts/UserContext';
import { markPerformance, Marks } from '@/client/utilities/performance/performanceMonitor';
import getClientHints from '@/shared/helpers/ClientHints';
import { getDeviceType } from '@/shared/helpers/DeviceDetector';
import { getStickyBannerContent } from '@/shared/helpers/GetStickyBannerContent';
import { getSSROptimizelyClient } from '@/tools/optimizely/ssrClient';
import { getOrCreatePulse } from '@/tools/tracking/utils';
/**
 * === READ ME ===
 * Next.js now supports local CSS imports,
 * so you can import the component CSS directly
 * in the file where it's used.
 *
 * Only add CSS here if it's truly global (e.g. used in both pages, always).
 *
 * https://nextjs.org/docs/basic-features/built-in-css-support#import-styles-from-node_modules
 */
import { LiraContainerIds } from '@/client/components/Adv/Lira/liraContainerIds';
import { GtmWithPerformanceImpact } from '@/shared/helpers/GtmPerformanceImpact';
import { BasePageProps } from '../types/basePageProps';

/** ^^^ READ ABOVE THE IMPORTS ^^^ */

markPerformance(Marks.GLOBAL_RUN_APP);
const optimizely = createOptimizelyInstance(process.env.NEXT_PUBLIC_INTERNAL_ENVIRONMENT);
export default class extends App<AppInitialProps<BasePageProps>> {
  static async getInitialProps(appContext: AppContext): Promise<AppInitialProps> {
    // Immediately begins initialisation of the server-side Optimizely, but we don’t wait here
    void getSSROptimizelyClient();
    const appInitial = await App.getInitialProps(appContext);
    const deviceType = getDeviceType(appContext.ctx.req);
    const clientHints = getClientHints(appContext.ctx.req);
    const {
      pageProps
    } = appInitial;
    const {
      query
    } = appContext.ctx;
    const categorySlug = query?.category;
    const stickyBannerContent = await getStickyBannerContent(pageProps.pageName || 'adview', categorySlug);
    return {
      pageProps: {
        ...pageProps,
        deviceType,
        clientHints,
        categorySlug,
        stickyBannerContent
      }
    };
  }
  componentDidCatch(error: Error | null) {
    captureException(error, {
      level: 'error',
      tags: {
        topLevelCatch: true
      }
    });
  }
  render() {
    const {
      Component,
      pageProps
    } = this.props;
    const {
      statusCode,
      clientHints,
      theme,
      pageName,
      deviceType,
      stickyBannerContent,
      categorySlug
    } = pageProps;
    const isServer = !isBrowser();
    let mainContent;
    if (statusCode !== HTTPStatusCode.OK) {
      if (statusCode === HTTPStatusCode.Gone) {
        mainContent = <FourTen categoryId={pageProps.categoryId} categoryLabel={pageProps.categoryLabel} categoryDefaultUrl={pageProps.categoryDefaultUrl} items={pageProps.items} />;
      } else {
        mainContent = <ErrorPage statusCode={statusCode} />;
      }
    } else {
      mainContent = <Component {...pageProps} />;
    }
    return <>
        <Head>
          {didomiScriptElements({
          env: process.env.NEXT_PUBLIC_INTERNAL_ENVIRONMENT
        })}
          {generateMetadata()}
          <meta name="viewport" content="width=device-width, initial-scale=1" />
        </Head>
        <GtmWithPerformanceImpact optimizely={optimizely} />
        <ClientHintsProvider {...clientHints}>
          <ViewportProvider defaultType={deviceType ?? 'mobile'}>
            {statusCode === HTTPStatusCode.OK && <TopTags pageName={pageName} />}
            <OptimizelySubitoProvider optimizely={optimizely} environmentId={getOrCreatePulse()?.getEnvironmentId()} isServerSide={isServer}>
              <WebVitalsLab pageName={pageName} />
              <UserProvider>
                <Layout theme={theme} pageName={pageName} categorySlug={categorySlug} stickyBannerContent={stickyBannerContent}>
                  {mainContent}
                </Layout>
              </UserProvider>
            </OptimizelySubitoProvider>
          </ViewportProvider>
        </ClientHintsProvider>
      </>;
  }
}
function TopTags(t0) {
  const $ = _c(9);
  const {
    pageName
  } = t0;
  const {
    isMobile,
    isDesktop
  } = useViewport();
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <style>{`
            #skinadvtop2 ~ body #${LiraContainerIds.adView.skin} {
              height: 0!important;
            }
        `}</style>;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== isMobile || $[2] !== pageName) {
    t2 = isMobile ? null : <div id={LiraContainerIds.adView.skin} style={{
      maxHeight: "122px",
      position: "sticky",
      textAlign: "center",
      top: 0,
      height: pageName === "listing" ? 90 : 0
    }} />;
    $[1] = isMobile;
    $[2] = pageName;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== isDesktop) {
    t3 = isDesktop ? <div id="apnx_preroll_wrapper" style={{
      position: "fixed",
      bottom: 0,
      right: 0,
      zIndex: 1
    }}><div id="apnx_preroll" style={{
        display: "none",
        width: "400px",
        marginRight: "20px",
        marginBottom: "73px",
        textAlign: "end",
        backgroundColor: "#fff"
      }} /><div id="apnx_preroll_close_button" style={{
        position: "absolute",
        top: "4px",
        right: "25px",
        width: "35px",
        height: "20px",
        zIndex: 2,
        display: "none",
        fontSize: "12px",
        fontWeight: 500,
        cursor: "pointer"
      }}>Chiudi</div></div> : null;
    $[4] = isDesktop;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <>{t1}{t2}{t3}</>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  return t4;
}